/**
 * 리플레이 기록과 재생.
 *
 * 클라이언트가 서버로 보내는 것은 이 구조 하나뿐이다. 상태는 전혀 보내지 않는다.
 * 서버는 같은 명부와 같은 입력으로 시뮬레이션을 다시 돌려 점수를 직접 계산한다.
 * 그래서 콘솔을 열어 숫자를 바꾸는 방식이 통하지 않는다.
 *
 * 다만 이 재생만으로는 명부 자체의 정직성을 알 수 없다. 없는 장비를 가졌다고
 * 주장하는 명부를 넣어도 시뮬레이션은 군말 없이 재현한다. 그래서 서버 검증은
 * 두 단계다. 먼저 명부가 계정의 실제 보유분과 맞는지 데이터베이스로 대조하고,
 * 그 다음 여기서 재생해 전과를 다시 계산한다. 앞 단계는 서버에만 있다.
 */
import { BATTLE_SEED_STEP, Trait, isRouteDepth, routeSegment, sanitizeRoute, traitMeritPct, traitsOfBattle } from './traits.ts';
import { CommandLog } from './commands.ts';
import type { Command } from './commands.ts';
import { FIELD_COLS, FIELD_ROWS, RAID_ROWS, battleTraits, createBattle } from './scenario.ts';
import type { ScenarioConfig } from './scenario.ts';
import { MAX_UNITS, TICK_HZ, Team } from './units.ts';
import type { World } from './world.ts';
import { FATIGUE_MAX } from './world.ts';
import { hashToHex } from './checksum.ts';
import { MAX_DEPTH } from './troops.ts';
import { meritScore } from '../game/score.ts';

/**
 * 규칙 버전.
 *
 * 병종 수치, 전투 판정, 조향, 지형 생성 등 시뮬레이션 결과에 영향을 주는 것을
 * 하나라도 고치면 이 값을 올린다. 예전 리플레이는 예전 규칙으로만 재현되므로
 * 버전이 다르면 검증을 시도하지 않고 거절한다.
 *
 * 이걸 빼먹으면 서버가 정직한 플레이어를 조작으로 몰아세운다.
 * 랭킹 시즌을 나눌 때 이 번호가 경계가 된다.
 *
 * 82: 진급 값 ⅔(`TIER_COST`, 경제 묶음). 전투는 진급 값을 읽지 않지만, 서버가 명부를 계정과 맞출 때(docs/design.md
 * 부록) 진급에 든 돈을 이 표로 센다 — 규칙의 번호는 하나로 둔다.
 * 83: 강화한 물건의 되팔 값에 강화에 들인 냥의 절반(`sellPrice`). 전투는 그대로, 계정 돈의 대조가 바뀐다.
 * 84: 장수 기술(일제사격 +50~110%·7초, 방진 레벨마다 −3%p·+0.5초)과 동료 기술(문턱 85%·85%, 맞붙는 셋 대기 25초) — 선택지 균형.
 * 85: 적 병사가 횡대로 걸음을 맞춰 온다(`LINE_RELEASE`·`FOE_LINE_X`), 전장 세로 56 → 32칸·장수가 쓰러진 뒤의 퇴각 50 → 30초,
 *     병사 체력 120%(`SOLDIER_HP_PCT`), 적 목표 격 90%·적장 결전 80%·깊이 20 너머 기울기 106(Todo11).
 * 86: 공격 명령의 궁수도 적 쪽으로 나아간다 — 칼이 닿기 전에는 지휘관 뒤 50px에서만(`BOW_REAR`·`meleeJoined`), 닿은 뒤에는
 *     다른 병사와 같은 끈으로(Todo11).
 * 87: 시작 군대의 궁수 3 → 1(도수 5·창 5·궁 1·민병 3, `createStartingArmy`). 전투는 그대로, 새 계정의 처음 명부가 바뀐다.
 * 88: 강화에 드는 재료가 단계마다 두 배(★1에 1 · ★2에 2 · ★3에 4, `enhanceMaterials`). 전투는 그대로, 계정의 창고 대조가 바뀐다.
 * 89: 대형의 기병 자리 — 간격 19 → 20px(앞뒤 줄 16 → 20px), 기병은 제 분대의 줄에서 보병 다음 자리에 선다(장수 부대의
 *     기병은 왼쪽 90px 밖 세 줄이었다, `SLOT_HORSE_STEP`·`placeSlots`). 궁수 줄은 분대의 마지막 줄에서 20px 뒤
 *     (`SLOT_BOW_CLEAR`, 줄이 셋 이상이거나 기병이 뒷줄에 설 때만 달라진다). 자리가 겹쳐 제자리에서 떨던 것을 고친다.
 * 90: 궁수가 표적을 나눠 겨눈다(겨누는 제 편 궁수 한 명마다 30px 더 먼 셈, `BOW_SPREAD`). 서서 지키는 궁수(대형·대기)는
 *     사거리 밖 150%까지 곡사로 쏜다 — 느리고 흩어지며 날아가는 동안에는 맞지 않는다(`BOW_LOB`, 투사체 상태 `pjLob`).
 * 91: 걷는 진의 병사·동료는 자리와 같은 걸음으로 따라 걷는다 — 지휘관이 지난 틱에 0.5px 넘게 걸었으면 닿았다고 서지 않고
 *     남은 만큼만 걷는다(`SLOT_MARCH`). 빠른 병사(기병)가 가다 서다 하던 것을 고친다.
 * 92: 장수가 선 뒤 헤매는 병사 — 바위에 가린 자리는 지휘관에게서 자리 쪽으로 20px 나간 점까지 돌아간 뒤에 간다
 *     (`slotDetour`), 서 있는 진에서 자리에 닿은 병사는 10px 벗어나기 전에는 되돌아 걷지 않는다(`slotHeld`), 동료는 제
 *     병사에게 밀리지 않는다(`relaxCrowd`).
 * 93: 제 자리가 바위·전장 밖이거나 지휘관에게서 가린 병사는 그 분대 줄 뒤의 빈 칸에 선다(`slotAlt` — 마지막 줄 다음 줄
 *     부터 열두 칸, 같은 분대의 다른 자리에서 18px 이상 떨어진 첫 칸; 걷든 서든 지난 틱의 칸을 먼저 본다).
 *     돌아가던 병사는 돌 곳 곁에서 자리가 보일 때만 그만 돈다.
 * 94: 공격 명령의 끈(`ATTACK_LEASH`·`ATTACK_LEASH_MAX`·`ATTACK_FLANK`)은 첫 칼이 닿기 전까지만 맨다(`meleeJoined`). 닿은 뒤로는
 *     부대원이 지휘관과의 거리를 재지 않고 적 쪽으로 나아간다 — 앞서 나갔다고 서지도, 멀어졌다고 돌아오지도 않는다.
 *     잔병 소탕 중(`mopUp`)에 걷는 아군은 포개진 몸을 떼어 놓을 때(`relaxCrowd`) 가려는 쪽(앞뒤)으로는 옮겨지지 않는다 —
 *     옆으로만 벌어진다. 무리 지어 쫓는 기병이 앞사람에게 걸려 느려지지도, 뒤에서 밀려 빨라지지도 않는다.
 * 95: 근접 유닛도 표적을 나눠 쫓는다(`MELEE_SPREAD`) — 걸어가야 하는 적을 이미 넷이 쫓고 있으면 다섯째부터 한 명마다 24px
 *     더 먼 것으로 친다(장수·적장·동료는 여덟까지), 쥔 표적은 12px 가깝게. 지금 닿는 적은 값을 매기지 않는다. 양쪽 모두.
 * 96: 용사(`ELITE`, troops.ts) — 명부의 우리 편 줄에 `elite`가 실리면 최고 티어·레벨 20 이상의 병사는 체력 120%·공격
 *     110%의 몸으로 선다(판마다 여덟까지). 이긴 판에서 쓰러진 용사는 70%가 부상으로 돌아온다(여느 병사 30%, `woundedRoll`).
 * 97: 용사의 임무(`Duty`, troops.ts) — 명부의 `duty`가 실린 용사는 분대의 명령 대신 지킬 것 곁에 선다. 호위는 그 사람
 *     (장수·동료) 둘레 110px의 적만 치고 그 사람을 노리는 적을 먼저, 궁수 보호는 그 부대 궁수 무리 앞에 서서 같은 식으로.
 * 98: 부하장수마다 다른 특성(`MATE_TRAITS`, troops.ts) — 잘 부리는 병종의 병사에게만 붙는 몫(공격 +6~20% · 방어 +1~3 ·
 *     사기 +20~60 가운데 하나), 레벨로 느는 지휘가 사람마다 기우는 쪽(공격·사기의 몫, 방어가 오르는 간격).
 * 99: 적의 용사(`FOE_ELITE`, troops.ts) — 깊이 10부터 하나, 다섯 깊이마다 하나씩 더(여덟까지). 그 판 적 명부의 가장 센
 *     근접 병종의 병사가 체력 120%·공격 110%·격 200%의 몸으로 서서 적장을 호위한다. 그리고 **용사는 무조건 호위한다**:
 *     궁수 보호 임무를 없애고, 우리 용사는 임무를 고르지 않아도 장수(명부의 `ward`가 부하장수면 그 사람) 곁에 선다.
 *     명부의 `duty`는 `ward`가 됐다.
 * 100: 용사는 **제가 든 부대의 대장**을 호위한다 — 0번 부대의 용사는 장수, 부하 부대의 용사는 그 부하장수. 명부의 `ward`를
 *     없앴다(줄의 `squad`가 곧 지킬 사람이다). 99까지는 어느 부대에 앉았든 장수(또는 명부가 적은 부하장수) 곁으로 갔다.
 * 101: 따로 공격하러 보낸 분대(공격 명령의 부하 부대)는 첫 칼이 닿기 전까지 주력의 맨 앞 병사보다 24px 넘게 앞서 나가지
 *     않는다(`ATTACK_ABREAST`, world.ts) — 앞쪽 걸음만 줄이고 옆걸음은 그대로. 칼이 닿으면 예전처럼 제 걸음이다.
 *     그리고 공격 명령을 받은 **기병 분대는 통째로 돌아간다**(`SWEEP`, world.ts): 지휘관이 적 벽의 가까운 쪽 끝 바깥을
 *     지나 적 궁수의 높이까지 가고 병사는 따른다. 가는 동안은 닿는 적만 친다.
 * 102: 대형의 기병 분대가 스스로 공격에 나선다(`sqAuto`, world.ts) — 장수 곁에 진을 짠 기병의 분대는 적 궁수가 줄 뒤에
 *     서면 명령이 공격으로 바뀌어 돌아간다. 플레이어가 그 분대에 명령을 내린 뒤로는 스스로 나서지 않는다.
 * 103: 돌아가는 기병 분대는 지휘관을 가운데 두고 뭉쳐 간다(`SWEEP_PACK`, world.ts) — 병사는 지휘관 뒤를 따르지 않고 가는
 *     쪽 50px 앞의 점 둘레로 모이고, 지휘관은 병사의 걸음에 맞춘다. 102까지는 지휘관이 100px쯤 앞서 혼자 화살을 받았다.
 * 104: 돌아가는 기병 분대는 **지휘관 둘레의 걷는 진**으로 간다 — 병사가 한 점으로 모이지 않고 대형의 제 자리를 따라
 *     걷는다(`placeSlots`). 줄에 한 번 서면 되돌아가지 않고(`sqSweepUp`), 올라갈 때는 바로 앞 96px만 겨눈다. 때 맞추기는
 *     지휘관의 걸음만 묶는다. 103에서는 병사들이 떨며 갔다(가는 쪽이 꺾이는 틱 13%).
 * 105: 돌아가는 분대의 옆걸음은 **줄 가까이의 바위 앞까지만** 가고 거기서 올라간다. 돌 곳이 바위 속이면 흐름을 탄다.
 *     104까지는 줄의 제 높이 지점이 바위 속이면 지휘관이 바위를 밀며 서 있었다(696판에 7판, 길게는 첫 칼 뒤 15초).
 * 106: 돌아가는 것은 그 분대의 **기병 병사와 지휘관뿐**이다 — 딸린 보병·궁수는 돌지 않고, 걸음·진·무게중심을 기병만으로
 *     잰다. **어디서든 칼이 닿으면 그만 돈다**(`meleeJoined`) — 첫 칼 뒤 15초의 상한(`sqSweepT`)은 없앴다. 105까지는
 *     보병 하나가 끼면 기병 무리가 보병 걸음으로 돌았고, 칼이 닿은 뒤에도 궁수의 높이까지 계속 돌았다.
 * 107: 돌아가는 무리를 표적으로 쥔 적의 근접 병사가 160px 안에 들면 **돌기를 그만두고 맞선다**(`SWEEP_MEET`). 106까지는
 *     칼이 닿을 때까지 진을 지키며 걸었다. **돌아가는 무리는 주력을 기다리지 않는다** — 때 맞추기(옛 `SWEEP.pacePct`)를
 *     없앴고, 돌기를 마친 분대의 기병과 지휘관은 첫 칼 전의 걸음 맞추기에서도 풀린다(`sqSweepFree`).
 * 108: **기본 명령이 공격이다**(30~107은 대형). 판을 열면 모든 부대가 공격으로 시작한다 — 주력이 대형으로 남아 늦게
 *     닿는 동안 먼저 돌아 들어간 기병이 혼자 녹던 것을 주력도 함께 들어가게 해 푼다(사용자).
 *     **공격의 지휘관이 돌격을 시작하는 거리가 450 → 150px**(`ATTACK_CHARGE`) — 첫 피해(화살 포함) 전에 적이 그보다
 *     멀면 제 분대의 가장 느린 걸음으로 간다. 장수만 혼자 튀어나가지 않게.
 * 109: **공격 분대의 병사도 첫 피해 전·적이 150px 밖이면 제 분대의 가장 느린 걸음으로 간다**(108은 지휘관만). 걸음이
 *     빠른 병사가 느린 병사를 두고 먼저 닿지 않는다. 돌아가는 기병 분대의 기병과 지휘관은 묶지 않는다(사용자).
 * 110: 병사 체력 120 → 138%(`SOLDIER_HP_PCT`) — 기본 명령이 공격이 된 뒤 짧아진 판을 싸우는 시간으로 늘린다(사용자).
 * 111: 돌아가는 기병 분대가 **줄까지 적 쪽으로 비스듬히 간다**(제 높이에서 옆으로만 가던 것을 벽의 앞과 같은 높이의
 *     줄 위 점을 겨눈다), 돌 줄을 벽 밖 130 → 78px로 당겼다(`SWEEP.outPx`) — 사용자.
 * 112: 돌아가는 기병 분대의 비스듬한 걸음을 **눕혔다** — 줄까지 남은 옆 거리의 절반만큼만 위를 겨눈다(옆 2에 위 1). 111은 벽의 앞
 *     높이를 곧장 겨눠 거의 위로 달렸고, 적 앞줄의 끝에 주력보다 먼저 닿아 기병과 부하장수를 크게 잃었다.
 * 113: **적은 레벨로만 자란다** — 깊이 30 너머의 단련 배율(체력·공격·격 ×)을 없앴다. 적장의 레벨도 깊이 30 너머에서
 *     휘하와 같이 99까지 오른다(`foeChiefLevel` — 112까지는 33에 머물렀다). 깊이 30까지는 그대로다(사용자).
 * 114: **적의 부대**(`FOE_SQUADS`) — 적 병사를 가로로 나눠 최대 8부대, 부대마다 대장(총대장 + 부장)이 서서 제 부대에
 *     지휘 보정(공격 105%·사기 +10)을 건다. 부장이 쓰러지면 그 부대만 기개의 60%를 잃는다. 깊이 30까지는 부장의 격만큼
 *     병사를 덜 세운다(예산 안에서 산다). 113까지는 적장 하나뿐이었고 지휘 보정이 없었다. 도는 기병 분대는 "적 궁수가
 *     앞줄에 섞였는가"를 쪽을 고르기 전에만 묻는다(틱마다 물어 돌기가 켜졌다 꺼졌다 했다).
 * 115: 부장이 쓰러질 때 그 부대의 병사가 잃는 기개 60 → 40%(`FOE_SQUADS.fallPct`) — 부장 하나에 부대가 통째로 달아나 판이
 *     짧아졌다(사용자).
 * 116: 첫 관문(깊이 5)까지는 적에게 부장이 없다(`FOE_SQUADS.subFromDepth` 6) — 깊이 5의 적이 두 부대(총대장 + 부장,
 *     병사 15)에서 한 부대(총대장, 병사 20)로 돌아간다. 시작 군대로 첫 관문을 넘는 판이 80% 아래였다(사용자).
 * 117: **부대별 용사**(`FOE_ELITE`) — 적의 용사가 부대마다 둘까지 서고(깊이 10에 둘, 다섯 깊이마다 둘씩 더, 열여섯까지)
 *     제 부대의 대장을 호위한다. 116까지는 판 전체에 여덟까지(깊이 10에 하나, 다섯 깊이마다 하나씩)였고 모두 총대장을
 *     지켰으며, 부대와 무관하게 명부의 가장 센 근접 줄에서 골랐다.
 * 118: **적장 결전도 부대를 나눈다** — 결전(관문 20·30…)의 적도 병사 수로 부대를 나누고, 가운데 세 부대(0·1·2번)의
 *     대장이 적장, 나머지 부대의 대장은 부장이다(값은 병사에서 치른다). 부장이 쓰러지면 그 부대만 흔들리고, 적장 셋이
 *     모두 쓰러지면(부장이 살아 있어도) 전군이 무너진다. 117까지 결전은 적장 셋의 세 부대뿐이었고 옆 적장은 가운데에서
 *     160px 떨어진 정해진 자리에 섰다.
 * 119: 적장 결전의 적 목표 격에 곱하던 80%를 없앴다(`WARLORDS_POWER_PCT`) — 결전(관문 20·30…)의 적 병사가 그만큼
 *     는다(신라 적: 깊이 20에 병사 63 → 76명·부대 다섯 → 여섯, 30에 86 → 104명·부대 여섯 → 여덟). 플레이한 사람: "결전 판의 적 병사 수 배율 자체를 없애자."
 * 120: 깊이 20 너머의 기울기 6 → 12%(`LATE_GROWTH_PCT` — 깊이 21부터 적 병사가 는다: 신라 적 깊이 25에 80 → 108명,
 *     30에 104 → 190명, 그 너머도 같은 만큼), 기마대의 목표 전투력 115 → 110%(`CAVALRY_POWER_PCT` — 관문 5 "적 선봉").
 *     둘 다 플레이한 사람이 정했다. 계단의 기준을 장비 + 함성을 얹은 판에 대기로 한 뒤의 값이다.
 * 121: 장수·부하장수의 활. 걷는 영웅이 활 아이템(단궁·각궁·맥궁·노·천보노)을 들면 궁수의 가지가 되어 쏜다 — 걷는 동안은
 *     쏘지 않고, 휩쓸지 않고, 화살을 다 써도 떠나지 않는다. 말 위의 활은 없는 것으로 친다. 활을 들지 않은 판은 한 틱도
 *     바뀌지 않는다. 전리품 표의 3·4티어에 활 셋이 들어 같은 주사위가 다른 물건을 낸다.
 * 122: 도적 무리 고르기(`ScenarioConfig.raidBand`, `RAID_BANDS`). 토벌에서 큰 무리를 고르면 수가 많고 레벨이 높은 도적과
 *     두령이 서고(새 병종 탈영병·마적 포함) 현상금이 크다. 번호가 없거나 0이면 예전의 그 판이다 — 출정과 가장 작은 무리의
 *     토벌은 한 틱도 바뀌지 않는다.
 * 123: 전리품이 는다 — 드롭 확률의 바닥 10 → 25%·천장 70 → 85%(`dropChancePct`), 이긴 판마다 두 번 굴린다(`DROP_ROLLS`).
 *     전투는 그대로이고 **이긴 판의 정산**이 바뀐다(같은 시드의 이긴 판이 다른 전리품을 낸다). 전멸 때의 소실 판정은
 *     그대로다 — 전리품은 이긴 판에만, 소실은 전멸한 판에만 굴려 두 주사위가 한 판에서 만나지 않는다.
 * 124: 토벌에서 물러날 수 있다 — 후퇴 명령으로 장수가 아래 끝에 닿으면 판을 접는다(`World.leaderFled`, 끝내는 쪽이
 *     본다). 살아서 못 이긴 토벌은 노획도 경험치도 없다(123까지는 잡은 만큼 받았다 — 화면에서는 나올 수 없던 판이다).
 *     전투의 틱은 그대로다.
 * 125: 물러난 토벌은 잡은 만큼 받는다 — 노획과 토벌 경험치(현상금은 두령을 벤 판에만). 124의 0냥을 되돌렸다(플레이한
 *     사람이 정했다). 전투의 틱과 재생의 멈춤(`replayOver`)은 그대로다.
 * 126: 앞쪽 도적 무리의 현상금(`RAID_BANDS`): 도적 소굴 300 → 500, 큰 산채 650 → 1,000, 마적단 1,400 → 1,600. 장부로
 *     잰 출정 한 번의 벌이에 맞췄다. 전투의 틱은 그대로다.
 */
export const REPLAY_VERSION = 126;

export interface ReplayResult {
  readonly checksum: string;
  readonly killScore: number;
  readonly deployed: number;
  readonly survivors: number;
  readonly leaderAlive: boolean;
  readonly merit: number;
}

export interface Replay {
  readonly version: number;
  readonly config: ScenarioConfig;
  readonly ticks: number;
  /** CommandLog.toJSON()의 평탄화 배열. */
  readonly commands: readonly number[];
  /** 클라이언트가 주장하는 결과. 서버는 이걸 믿지 않고 다시 계산한다. */
  readonly claimed?: ReplayResult;
}

function deployedCount(cfg: ScenarioConfig): number {
  let n = 0;
  for (const e of cfg.roster) n += e.count;
  return n;
}

/**
 * 명부의 출정 맥락(`runDeployed`·`resting`)이 사람 수로 말이 되는지 — 0 이상 전장 정원(`MAX_UNITS`) 이하의 정수. 아니면
 * 없는 것으로 친다. 처음에는 정수인지만 보아, `1e308`을 실은 명부가 상한 비교를 반올림으로 지나 전과가 NaN이 됐다
 * (리뷰가 짚었다). 한 출정에 데려가는 병사는 전장 정원을 넘을 수 없다.
 */
function count(v: unknown): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= MAX_UNITS ? v : null;
}

export function summarize(world: World, cfg: ScenarioConfig): ReplayResult {
  // 생환은 출정 전체로 센다 — 분모는 출정에 처음 데려간 수(`runDeployed`), 분자에는 이 판을 쉰 병사(`resting`)가 든다.
  // 정산(`applyBattle`)과 같은 식이다. 명부만 세면 둘째 판부터·협곡에서 전과가 갈렸다(리뷰가 짚었다).
  const deployed = Math.max(deployedCount(cfg), count(cfg.runDeployed) ?? 0);
  // 빠져나간 병사도 산 것이다. 정산(`applyBattle`)과 HUD가 같은 함수를 쓴다.
  //
  // 여기만 `alive`를 세고 있었다. 후퇴로 빠져나간 병사가 서버에서는 전부 죽은
  // 것으로 잡혀, 화면과 계정에는 맞는 전과가 서버 재생에서만 낮게 나왔다. 정직한
  // 플레이어가 "전과 불일치"로 거절당하는 자리다. 리뷰가 잡았다.
  const survivors = world.soldiersSurviving(Team.Player) + (count(cfg.resting) ?? 0);
  const leaderAlive = world.leaderAlive();
  const killScore = world.stats.killScore[Team.Player];
  return {
    checksum: hashToHex(world.checksum()),
    killScore,
    deployed,
    survivors,
    leaderAlive,
    // 토벌에는 전과가 붙지 않는다. 클라이언트(applyBattle)도 0을 기록한다.
    //
    // 분기를 빼먹으면 서버가 혼자 다른 답을 낸다. 토벌은 deployed가 0이라
    // 생환 배율이 바닥값 25%로 떨어지고, 전과가 killScore의 4분의 1만큼
    // 붙어 버린다. 그러면 0을 보낸 정직한 플레이어는 "전과 불일치"로
    // 거절당하고, 그 값을 그대로 보낸 쪽은 병사를 한 명도 걸지 않은 싸움으로
    // 랭킹에 오른다. 토벌에서 전과를 뗀 이유가 서버에서 무너진다.
    merit: cfg.raid
      ? 0
      : meritScore({ killScore, deployed, survivors, leaderAlive, depth: cfg.depth, bonusPct: traitMeritPct(battleTraits(cfg)) }),
  };
}

/**
 * **기록보다 먼저 판이 끝났는가**(재생을 멈추는 틱). 화면이 판을 끝내는 조건 가운데 기록의 틱 수와 따로 지켜야 하는 것:
 * - 장수가 쓰러진 뒤의 퇴각이 끝났다(`World.fallOver`) — 틱 수를 늘려 낸 기록이 더 돌아 빠져나간 병사를 늘리지 못하게.
 * - 토벌에서 장수가 물러났다(`World.leaderFled`, 124) — 화면은 그 틱에 판을 접는다(번 돈 없음). 여기서 멈추지 않으면
 *   접힌 토벌의 기록에 공격 명령을 덧붙여 더 싸우고 두령을 벤 것으로 낼 수 있었다(리뷰가 재현했다: 검증을 통과하고
 *   정산이 현상금을 준다).
 * 재생하는 쪽(서버 `runReplay`·화면의 다시 보기 `ReplayPlayer`)이 모두 이것을 본다.
 */
export function replayOver(world: World, cfg: ScenarioConfig): boolean {
  // 판을 세우는 쪽(`createBattle`)과 같은 눈으로 본다(참인 값이면 토벌) — 처음에는 `=== true`로 봐서, 명부의 `raid`를 1이나
  // "true"로 적은 기록은 토벌로 서고도 이 멈춤을 비켜 갔다(리뷰가 재현했다). 그런 값은 문에서도 거른다(`verifyReplay`).
  return world.fallOver() || (Boolean(cfg.raid) && world.leaderFled());
}

/** 리플레이를 처음부터 끝까지 재생한다. 브라우저와 서버가 같은 함수를 쓴다. */
export function runReplay(replay: Replay): { world: World; result: ReplayResult } {
  const world = createBattle(replay.config);
  const log = CommandLog.fromJSON(replay.commands);
  const buf: Command[] = [];
  for (let t = 0; t < replay.ticks; t++) {
    log.take(t + 1, buf);
    world.step(buf);
    // 화면이 판을 끝낸 틱에서 멈춘다(`replayOver`) — 틱 수를 늘려 낸 리플레이가 더 돌지 못하게.
    if (replayOver(world, replay.config)) break;
  }
  return { world, result: summarize(world, replay.config) };
}

/**
 * 리플레이 크기의 상한. 서버가 믿지 않는 JSON을 돌리기 전에 거른다.
 *
 * 월드는 경로 지점을 부대당 16개만 담지만, 그것은 담기는 양이지 받는 양이 아니다.
 * 거대한 커맨드 배열이나 끝없는 틱 수는 담기기 전에 풀리고 도는 것만으로 서버를
 * 붙잡는다. 한 전투가 15분을 넘을 일은 없고(목표 60~90초), 사람이 한 틱에 하나꼴로
 * 입력해도 커맨드는 틱 수를 넘지 않는다. 넉넉히 두 배를 둔다.
 */
export const MAX_REPLAY_TICKS = TICK_HZ * 60 * 15;
export const MAX_REPLAY_COMMANDS = MAX_REPLAY_TICKS * 2;

/** 서버 쪽 검증. 주장된 결과와 재계산 결과가 어긋나면 거부한다. */
export interface Verdict {
  ok: boolean;
  /**
   * 다시 돌려 얻은 값. 버전이 달라 아예 돌려보지 못했으면 null이다.
   *
   * 예전에는 여기에 `null as never`를 넣어 타입만 맞춰 뒀다. 그러면 부르는 쪽이
   * 버전 거절을 결과가 있는 실패와 똑같이 다루다가 그대로 터진다. 실제로
   * `npm run verify`가 규칙 버전을 올린 다음 그렇게 죽었다.
   */
  computed: ReplayResult | null;
  reason?: string;
}

/**
 * 한 출정의 판들이 구간마다 같은 길을 갔는가(갈림길, `Route`). 한 판의 검증(`verifyReplay`)은 그 판의 길과 특성만 맞춰
 * 본다 — 같은 출정 시드로 깊이 16은 산골, 17은 들길을 실은 두 리플레이가 저마다 통과했다(리뷰가 짚었다). "다음 관문까지
 * 바꿀 수 없다"는 규칙은 판 하나로는 볼 수 없으므로, 한 출정의 판을 모아 받는 쪽(서버의 계정 대조)이 이것으로 막는다.
 * 판 시드에서 출정 시드를 되짚어(`BATTLE_SEED_STEP`) 출정과 구간(`routeSegment`)으로 묶는다. 토벌·갈림길 밖의 판은 본다.
 */
export function verifyRunRoutes(configs: readonly ScenarioConfig[]): { ok: boolean; reason?: string } {
  const chosen = new Map<string, string>();
  for (const cfg of configs) {
    if (cfg.raid || !isRouteDepth(cfg.depth)) continue;
    const route = sanitizeRoute(cfg.route);
    if (route === null) return { ok: false, reason: `깊이 ${cfg.depth}: 갈림길에서 고른 길이 없습니다` };
    const key = `${cfg.seed - cfg.depth * BATTLE_SEED_STEP}:${routeSegment(cfg.depth)}`;
    const before = chosen.get(key);
    if (before !== undefined && before !== route) {
      return { ok: false, reason: `깊이 ${cfg.depth}: 같은 구간에서 길을 바꿨습니다` };
    }
    chosen.set(key, route);
  }
  return { ok: true };
}

export function verifyReplay(replay: Replay): Verdict {
  if (replay.version !== REPLAY_VERSION) {
    return {
      ok: false,
      computed: null,
      reason: `리플레이 버전이 다릅니다 (기록 ${replay.version}, 지금 ${REPLAY_VERSION})`,
    };
  }
  // `raid`는 참·거짓(또는 없음)이어야 한다. 믿지 않는 JSON은 1이나 "true"를 실어 올 수 있고, 코드의 자리마다 그 값을
  // 다르게 읽으면(참인 값인가 / 정확히 true인가) 한 판이 토벌이면서 토벌이 아니게 된다(리뷰가 그 틈으로 물러난 토벌의
  // 멈춤을 비켜 갔다).
  const raidFlag: unknown = replay.config.raid;
  if (raidFlag !== undefined && typeof raidFlag !== 'boolean') {
    return { ok: false, computed: null, reason: '명부의 토벌 표시가 참·거짓이 아닙니다' };
  }
  // 병사를 걸지 않은 싸움은 토벌뿐이다.
  //
  // `survivalPct`는 `deployed`가 0이면 바닥값 25%를 돌려준다. 화면은 병력 0
  // 출정을 막지만 서버가 상대하는 것은 JSON이다. `raid`를 빼고 명부를 비운
  // 리플레이를 보내면 아무도 걸지 않고 killScore의 4분의 1을 전과로 받는다.
  // 토벌 쪽은 merit를 0으로 못 박아 막았으니, 남은 문은 이쪽이다.
  if (!replay.config.raid && deployedCount(replay.config) === 0) {
    return { ok: false, computed: null, reason: '출전 명부가 비어 있습니다' };
  }
  // 토벌은 장수 혼자다. 동료가 병사 없는 분대에도 서게 된 뒤로(단신 출전) 토벌 명부에 동료를 실으면 장수와
  // 동료 셋이 도적을 쳐 벤 격(→ 토벌 경험치·현상금)을 키운다. 예전에는 병사 없는 분대의 동료를 세우지 않아
  // 저절로 막혔다(리뷰가 짚었다). 화면은 늘 빈 명단을 보낸다.
  if (replay.config.raid && (replay.config.companions ?? []).some(Boolean)) {
    return { ok: false, computed: null, reason: '토벌에는 부하장수가 나가지 않습니다' };
  }
  // 깊이는 1~MAX_DEPTH의 정수다. `depthRoster`가 범위 밖을 조용히 접으므로 여기서 막지 않으면
  // "깊이 31" 리플레이가 깊이 30의 명부로 재생돼 통과한다(리뷰가 짚었다). 게임의 상한(`advance`)은
  // 화면 쪽 문이고, 서버가 상대하는 것은 JSON이다.
  const depth = replay.config.depth;
  if (!Number.isInteger(depth) || depth < 1 || depth > MAX_DEPTH) {
    return { ok: false, computed: null, reason: `깊이가 범위를 벗어났습니다 (1~${MAX_DEPTH})` };
  }
  // 전장의 크기는 규칙이다(출정 `FIELD_ROWS`, 토벌 `RAID_ROWS`). 명부에 적힌 크기로 지형과 출발 거리를 세우므로,
  // 다른 크기를 실으면 다른 판이 된다 — 옛 32칸을 실은 출정은 맞붙기 전의 행군을 건너뛰고도 해시가 맞아 통과했다
  // (리뷰가 재현했다: 같은 입력으로 전과 442 대 466). 넓은 평원·좁은 협곡의 폭은 `createBattle`이 특성에서 내므로
  // 명부의 폭은 늘 `FIELD_COLS`다. 검산 도구는 `createBattle`을 직접 불러 아무 크기나 세워도 된다.
  const wantRows = replay.config.raid ? RAID_ROWS : FIELD_ROWS;
  if (replay.config.cols !== FIELD_COLS || replay.config.rows !== wantRows) {
    return { ok: false, computed: null, reason: `전장 크기가 규칙과 다릅니다 (${FIELD_COLS}×${wantRows})` };
  }
  // 전장 특성은 판의 시드·깊이가 정한다(`traitsOfBattle`). 명부에 다른 특성을 실으면 적·지형·전과가 달라진다 —
  // 관문에 늪지·인해를 실어 전과를 부풀린 리플레이가 거르기(`sanitizeTraits`)만으로는 통과했다(리뷰가 짚었다).
  // 판의 시드 자체가 그 출정의 것인지(한 출정의 판들이 같은 출정 시드를 쓰는지)는 서버의 계정 대조가 본다.
  if (!replay.config.raid) {
    // 갈림길 구간이면 고른 길이 있어야 한다(`isRouteDepth`). 어느 길이든 정직한 선택이다 — 길마다 다시 낸 특성과 맞춘다.
    const route = sanitizeRoute(replay.config.route);
    if (isRouteDepth(depth) && route === null) {
      return { ok: false, computed: null, reason: '갈림길에서 고른 길이 없습니다' };
    }
    const want = traitsOfBattle(replay.config.seed, depth, route);
    const got = battleTraits(replay.config);
    const raw = replay.config.traits ?? [];
    if (want.length !== got.length || want.some((t, k) => got[k] !== t) || raw.length !== got.length) {
      return { ok: false, computed: null, reason: '전장 특성이 출정·깊이와 맞지 않습니다' };
    }
  }
  // 명부의 피로(`RosterEntry.fatigue`, Todo6)는 정수 0~`FATIGUE_MAX`, 줄의 병사 수를 넘지 않는다. 판은 문 앞에서 잘라 세우지만
  // (`setFatigue`), 모양이 틀린 명부는 들이지 않는다. 그 값이 그 출정의 앞선 판에서 나온 것인지는 한 판으로는 볼 수 없다 —
  // 서버의 출정 대조(판들을 이어 uid별로)가 본다(docs/design.md).
  for (const e of replay.config.roster ?? []) {
    const f = (e as { fatigue?: unknown }).fatigue;
    if (f === undefined) continue;
    if (!Array.isArray(f) || f.length > e.count || f.some((v) => !Number.isInteger(v) || v < 0 || v > FATIGUE_MAX)) {
      return { ok: false, computed: null, reason: '명부의 피로가 올바르지 않습니다' };
    }
  }
  // 출정 맥락. 쉰 병사는 좁은 협곡에서만 있고, 명부와 쉰 병사를 더해도 출정에 처음 데려간 수를 넘지 않는다 — 넘기면
  // 생환이 100%를 넘게 부풀린다.
  const runDeployed = replay.config.runDeployed;
  const resting = replay.config.resting;
  if (runDeployed !== undefined && count(runDeployed) === null) {
    return { ok: false, computed: null, reason: '출정 병력이 올바르지 않습니다' };
  }
  if (resting !== undefined && count(resting) === null) {
    return { ok: false, computed: null, reason: '쉰 병사 수가 올바르지 않습니다' };
  }
  if (resting !== undefined && resting !== 0) {
    const rest = count(resting);
    if (rest === null || !battleTraits(replay.config).includes(Trait.Canyon)) {
      return { ok: false, computed: null, reason: '쉰 병사는 좁은 협곡에서만 있습니다' };
    }
    if (deployedCount(replay.config) + rest > (count(runDeployed) ?? 0)) {
      return { ok: false, computed: null, reason: '쉰 병사가 출정 병력을 넘습니다' };
    }
  }
  if (!Number.isInteger(replay.ticks) || replay.ticks < 0 || replay.ticks > MAX_REPLAY_TICKS) {
    return { ok: false, computed: null, reason: '틱 수가 범위를 벗어났습니다' };
  }
  if (!Array.isArray(replay.commands) || replay.commands.length > MAX_REPLAY_COMMANDS * 4) {
    return { ok: false, computed: null, reason: '커맨드가 너무 많습니다' };
  }
  const { result } = runReplay(replay);
  const claimed = replay.claimed;
  if (!claimed) return { ok: true, computed: result };
  if (claimed.checksum !== result.checksum) {
    return { ok: false, computed: result, reason: '상태 해시 불일치' };
  }
  if (claimed.merit !== result.merit) {
    return { ok: false, computed: result, reason: '전과 불일치' };
  }
  return { ok: true, computed: result };
}
